import { FC, RefObject, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { genreAsGameLike } from '../../helpers/genreAsGameLike'
import { CompareTab } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { GameCreativeDetailsData, GenreCreativeAggregate } from '../../types'
import { CompareOverviewTab } from '../CompareOverviewTab/CompareOverviewTab'
import { CompareStoryPatternsTab } from '../CompareStoryPatterns/CompareStoryPatternsTab'
import { ComparisonDiagnosis } from '../ComparisonDiagnosis/ComparisonDiagnosis'
import { ComparisonRadarChart } from '../ComparisonRadarChart/ComparisonRadarChart'
import { CreativeTagsByGroupSection } from '../CreativeTagsByGroupSection/CreativeTagsByGroupSection'
import { CrownJewelsTable } from '../CrownJewelsTable/CrownJewelsTable'
import { SectionCard } from '../SectionCard/SectionCard'

/**
 * Comparison target on the right side of the matchup. Exactly one variant is
 * active: another game (`kind: 'game'`) or a whole genre (`kind: 'genre'`).
 */
export type CompareTarget = { kind: 'game'; data: GameCreativeDetailsData; gameId: string } | { kind: 'genre'; data: GenreCreativeAggregate; genreId: string }

interface CompareResultBodyProps {
  tab: CompareTab
  /** Source game (left side of the matchup). */
  source: GameCreativeDetailsData
  sourceGameId: string
  target: CompareTarget
  /** Switch the active tab (used by the Overview "View all" affordances). */
  onSelectTab: (tab: CompareTab) => void
  /** Switch to Creative Tags scrolled to a tag (Overview shared-tag preview). */
  onOpenCreativeTag: (tagId: string) => void
  /** Id of the tag to scroll to on the Creative Tags tab (set by `onOpenCreativeTag`). */
  pendingTagScroll: string | null
  /** Clears the pending scroll target once the section has handled it. */
  onScrolledToTag: () => void
  /** Scroll container the Story Patterns / Creative Tags tabs reset on drill-down. */
  scrollContainerRef?: RefObject<HTMLElement | null>
}

/**
 * Renders the active Compare tab body (Overview / Motivations / Story Patterns
 * / Creative Tags) for both the game-vs-game and game-vs-genre matchups. The
 * genre target is wrapped in a `GameCreativeDetailsData` shape via
 * `genreAsGameLike` so the per-game Overview and Creative Tags components need
 * no genre-specific branch; the Motivations and Story Patterns tabs read the
 * genre fields directly.
 */
export const CompareResultBody: FC<CompareResultBodyProps> = ({
  tab,
  source,
  sourceGameId,
  target,
  onSelectTab,
  onOpenCreativeTag,
  pendingTagScroll,
  onScrolledToTag,
  scrollContainerRef,
}) => {
  const { genreLabel } = useUATaxonomy()
  const targetGameId = target.kind === 'game' ? target.gameId : null
  const targetGenreId = target.kind === 'genre' ? target.genreId : null
  // A genre target stands in for a game here, so its name is display copy and
  // has to be localised; a real game's name never is.
  const targetLabel = target.kind === 'game' ? target.data.game.name : genreLabel(target.data.genre)
  const targetAsGame = useMemo<GameCreativeDetailsData>(
    () => (target.kind === 'game' ? target.data : genreAsGameLike(target.data, targetLabel)),
    [target, targetLabel]
  )
  const targetIconUrl = target.kind === 'game' ? target.data.game.icon : null

  const motivationSource = { name: source.game.name, icon: source.game.icon, scores: source.motivationScores.creativeAds }
  const motivationTarget =
    target.kind === 'game'
      ? { name: target.data.game.name, icon: target.data.game.icon, scores: target.data.motivationScores.creativeAds }
      : { name: genreLabel(target.data.genre), icon: undefined, scores: target.data.creativeAdsMotivations }
  // Both payloads are scoped to the same network/month, so either side's
  // scope-wide leader works as the shared Ad Performance anchor.
  const scopeMaxInstallShare = source.scopeMaxInstallShare

  return (
    <>
      {tab === 'overview' && (
        <CompareOverviewTab
          source={source}
          target={targetAsGame}
          sourceGameId={sourceGameId}
          targetGameId={targetGameId}
          targetGenreId={targetGenreId}
          onSelectTab={onSelectTab}
          onOpenCreativeTag={onOpenCreativeTag}
        />
      )}
      {tab === 'motivations' && <CompareMotivationsTab source={motivationSource} target={motivationTarget} />}
      {tab === 'storyPatterns' && (
        <CompareStoryPatternsTab
          sourceGameId={sourceGameId}
          targetGameId={targetGameId}
          targetGenreId={targetGenreId}
          targetLabel={targetLabel}
          sourceLabel={source.game.name}
          sourceIconUrl={source.game.icon}
          targetIconUrl={targetIconUrl}
          scrollContainerRef={scrollContainerRef}
        />
      )}
      {tab === 'creativeTags' && (
        <CompareCreativeTagsTab
          sourceData={source}
          targetData={targetAsGame}
          scopeMaxInstallShare={scopeMaxInstallShare}
          scrollToTag={pendingTagScroll}
          scrollContainerRef={scrollContainerRef}
          onScrolledToTag={onScrolledToTag}
        />
      )}
    </>
  )
}

interface MotivationSeries {
  name: string
  /** Entity icon URL used as the dumbbell endpoint; `undefined` for genres. */
  icon?: string
  scores: GameCreativeDetailsData['motivationScores']['creativeAds']
}

interface CompareMotivationsTabProps {
  source: MotivationSeries
  target: MotivationSeries
}

/**
 * Motivations tab body: a Creative Ads radar followed by the cross-entity
 * Motivation Alignment diagnosis. Both surfaces are fed the Creative Ads
 * motivation scores on each side; `ComparisonDiagnosis` is signal-agnostic
 * (it only checks each score against the medium-intensity threshold) so the
 * same body serves game-vs-game and game-vs-genre without branching.
 */
const CompareMotivationsTab: FC<CompareMotivationsTabProps> = ({ source, target }) => {
  const { t } = useTranslation('ua-insights')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionCard title={t('compare_motivation_diagnosis_title')}>
        <ComparisonDiagnosis source={source} target={target} unavailableMessage={t('compare_diagnosis_unavailable')} />
      </SectionCard>
      <SectionCard title={t('compare_motivations_title', { source: source.name, target: target.name })}>
        <ComparisonRadarChart source={source} target={target} unavailableMessage={t('compare_radar_unavailable')} />
      </SectionCard>
    </Box>
  )
}

interface CompareCreativeTagsTabProps {
  sourceData: GameCreativeDetailsData
  targetData: GameCreativeDetailsData
  scopeMaxInstallShare: number
  scrollToTag?: string | null
  scrollContainerRef?: RefObject<HTMLElement | null>
  onScrolledToTag?: () => void
}

/**
 * Creative Tags tab body. Reuses `CreativeTagsByGroupSection` (per-group
 * drill-down) and `CrownJewelsTable` (per-game synthesis). The Ad
 * Performance scale is anchored on the strongest individual ad across every
 * game on the selected network/month — not just the two on screen — so a
 * tag's perf% represents its standing within its competitive market segment.
 */
const CompareCreativeTagsTab: FC<CompareCreativeTagsTabProps> = ({
  sourceData,
  targetData,
  scopeMaxInstallShare,
  scrollToTag,
  scrollContainerRef,
  onScrolledToTag,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <CreativeTagsByGroupSection
        source={sourceData}
        target={targetData}
        scopeMaxInstallShare={scopeMaxInstallShare}
        scrollToTag={scrollToTag}
        scrollContainerRef={scrollContainerRef}
        onScrolledToTag={onScrolledToTag}
      />
      <CrownJewelsTable source={sourceData} target={targetData} scopeMaxInstallShare={scopeMaxInstallShare} />
    </Box>
  )
}
